import Interpolate from '@doist/react-interpolate';
import { useContext, useEffect, useState } from 'react';
import AppContext from '../../app-context';
import ActionButton from '../common/action-button';
import CloseButton from '../common/close-button';
import GiftCard from '../common/gift-card';
import GiftDetailsToggle from '../common/gift-details-toggle';
import InputForm from '../common/input-form';
import { ValidateInputForm } from '../../utils/form';
import { getSiteDateString } from '../../utils/date-time';
import {
  getGiftDurationLabel,
  getGiftIntroduction,
  getGiftRedemptionErrorMessage,
} from '../../utils/gift-redemption-notification';
import { t } from '../../utils/i18n';
import useCardTilt from '../../utils/use-card-tilt';

export const BetaGiftRedemptionStyles = `
.gh-portal-gift-redemption-form {
    margin-top: 24px;
}

.gh-portal-gift-redemption-form + .gh-portal-gift-checkout-cta {
    margin-top: 16px;
}

/* An already-signed-in redeemer gets no form, so without this the message
   block would sit flush against the button. */
.gh-portal-gift-redemption-message + .gh-portal-gift-checkout-cta {
    margin-top: 24px;
}

.gh-portal-gift-redemption-message {
    margin-top: 24px;
    padding: 16px 20px;
    background: var(--grey13);
    border-radius: 8px;
}

.gh-portal-gift-redemption-message-text {
    margin: 0;
    font-size: 1.6rem;
    line-height: 1.5em;
    font-style: italic;
    color: var(--grey1);
    white-space: pre-line;
    overflow-wrap: anywhere;
}

.gh-portal-gift-redemption-message-from {
    margin: 8px 0 0;
    font-size: 1.4rem;
    color: var(--grey6);
}
`;

const BetaGiftRedemptionPage = () => {
  const { action, brandColor, doAction, member, pageData, site } = useContext(AppContext);
  const gift = pageData?.gift;
  const isLoggedIn = !!member;
  const [name, setName] = useState(member?.name || gift?.recipient_name || '');
  const [email, setEmail] = useState(member?.email || '');
  const [errors, setErrors] = useState({});
  const [showDetails, setShowDetails] = useState(false);
  const { cardRef, containerProps: cardTiltProps } = useCardTilt();

  useEffect(() => {
    // Prefill with the recipient name the buyer entered, so the gift card
    // is personal before the recipient types anything.
    setName(member?.name || gift?.recipient_name || '');
    setEmail(member?.email || '');
    setErrors({});
  }, [member?.email, member?.name, gift?.recipient_name]);

  useEffect(() => {
    if (gift) {
      return;
    }

    doAction('openNotification', {
      action: 'giftRedemption:failed',
      status: 'error',
      autoHide: false,
      closeable: true,
      message: getGiftRedemptionErrorMessage({ code: 'GIFT_NOT_FOUND' }),
    });
    doAction('closePopup');
  }, [doAction, gift]);

  if (!gift) {
    return null;
  }

  const formFields = [
    {
      type: 'text',
      value: name,
      placeholder: t('Jamie Larson'),
      label: t('Your name'),
      name: 'name',
      required: false,
      errorMessage: errors.name || '',
      tabIndex: 1,
      // If the buyer already supplied the recipient's name, land the cursor
      // on the email — the one field they still need to fill.
      autoFocus: !name && !email,
    },
    {
      type: 'email',
      value: email,
      placeholder: t('jamie@example.com'),
      label: t('Your email'),
      name: 'email',
      required: true,
      errorMessage: errors.email || '',
      tabIndex: 2,
      autoFocus: !!email || (!!name && !email),
    },
  ];

  const handleFieldChange = (event, field) => {
    setErrors((currentErrors) => ({
      ...currentErrors,
      [field.name]: '',
    }));

    if (field.name === 'name') {
      setName(event.target.value);
    }

    if (field.name === 'email') {
      setEmail(event.target.value);
    }
  };

  const handleKeyDown = (event) => {
    if (event.keyCode === 13) {
      if (isRedeeming) {
        return;
      }

      handleRedeemClick(event);
    }
  };

  const handleRedeemClick = (event) => {
    event.preventDefault();

    if (isRedeeming) {
      return;
    }

    if (isLoggedIn) {
      doAction('redeemGift', {
        giftToken: pageData?.token,
      });
      return;
    }

    const formErrors = ValidateInputForm({ fields: formFields });
    const hasErrors = Object.values(formErrors).some((errorMessage) => !!errorMessage);

    setErrors(formErrors);

    if (hasErrors) {
      return;
    }

    doAction('redeemGift', {
      email,
      name,
      giftToken: pageData?.token,
    });
  };

  const isRedeeming = action === 'redeemGift:running';
  const buttonLabel = isRedeeming ? t('Redeeming...') : t('Redeem your gift');
  const siteIcon = site?.icon;
  const siteTitle = site?.title || '';
  const buyerName = gift.buyer_name || '';
  const tierName = gift.tier.name;
  const giftDetails = {
    buyerName: <strong>{buyerName}</strong>,
    duration: <strong>{gift.duration}</strong>,
    tierName: <strong>{tierName}</strong>,
    siteTitle,
  };
  const headerText = getGiftIntroduction({
    buyerName,
    cadence: gift.cadence,
    duration: gift.duration,
    siteTitle,
  });
  const expiryLabel = gift.expires_at
    ? getSiteDateString(gift.expires_at, { locale: site?.locale, timezone: site?.timezone })
    : '';
  const benefits = gift.tier.benefits || [];
  const tierDescription = gift.tier.description || '';

  return (
    <>
      <div className="gh-portal-content giftRedemption">
        <CloseButton />
        <div className="gh-portal-gift-checkout">
          <div className="gh-portal-gift-checkout-left">
            <div className="gh-portal-gift-checkout-bg" aria-hidden="true" />
            <div className="gh-portal-gift-checkout-inner">
              <header className="gh-portal-gift-checkout-header">
                <h1 className="gh-portal-main-title">{t('A gift, just for you')}</h1>
                <p className="gh-portal-gift-checkout-subtitle">
                  <Interpolate mapping={giftDetails} string={headerText} />
                </p>
              </header>

              {gift.message && (
                <div className="gh-portal-gift-redemption-message" data-testid="gift-message">
                  <p className="gh-portal-gift-redemption-message-text">
                    &ldquo;{gift.message}&rdquo;
                  </p>
                  {buyerName && (
                    <p className="gh-portal-gift-redemption-message-from">&mdash; {buyerName}</p>
                  )}
                </div>
              )}

              {!isLoggedIn && (
                <div className="gh-portal-gift-redemption-form">
                  <InputForm
                    fields={formFields}
                    onChange={handleFieldChange}
                    onKeyDown={handleKeyDown}
                  />
                </div>
              )}

              <ActionButton
                brandColor={brandColor}
                classes="gh-portal-gift-checkout-cta"
                label={buttonLabel}
                onClick={handleRedeemClick}
                style={{ width: '100%' }}
                disabled={isRedeeming}
                isRunning={isRedeeming}
              />

              {expiryLabel && (
                <p className="gh-portal-gift-checkout-cta-note">
                  {t('This gift can only be redeemed once and expires on {expiryDate}.', {
                    expiryDate: expiryLabel,
                  })}
                </p>
              )}
            </div>
          </div>

          <div className="gh-portal-gift-checkout-right" {...cardTiltProps}>
            <div className="gh-portal-gift-checkout-right-panel">
              <div className="gh-portal-gift-checkout-card-stack" data-revealing={showDetails}>
                <GiftCard
                  cardRef={cardRef}
                  duration={getGiftDurationLabel(gift)}
                  tierName={gift.tier.name}
                  toName={name.trim() || null}
                  fromName={buyerName || null}
                  siteIcon={siteIcon}
                  siteTitle={siteTitle}
                />

                <GiftDetailsToggle
                  description={tierDescription}
                  benefits={benefits}
                  showDetails={showDetails}
                  onToggle={() => setShowDetails((s) => !s)}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default BetaGiftRedemptionPage;
